import { useRef } from 'react'

import { IActionRepeaterValue, TypographyGroupValue } from '@makyoapp/makyo-common'
import { Alignment, ColorVariant, Elevation, FABCornerRadius, HoverEffectVariant } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'

import { ICompFabProps } from './comp'

export interface CompFabWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: Partial<ICompFabProps>) => React.ReactNode
}

/**
 * CompFabWrapperCore
 */
export function CompFabWrapperCore({
  infos: { settings, containerClient, detailTableId, detailRowIndex, activeLayer, component },
  action: { simulateAction },
  children,
}: CompFabWrapperCoreProps) {
  const { openComponentSettings, isDevelopmentMode, setActiveComponentAccordion } = useAppBuilderContext()
  const { setAccordions } = useBuilderAccordionContext()

  const primaryElRef = useRef<any>(null)
  const secondaryElRef = useRef<any>(null)

  const isOverlayActive = activeLayer.type === 'overlay'
  const isHidden = false // isOverlayActive

  // ========================= data =========================
  const primaryIcon = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.Fab1DataIcon],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const primaryLabel = useParseVariableText(
    settings?.[ComponentSettingKey.Fab1DataLabel],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const primaryBadge = useParseVariableText(
    settings?.[ComponentSettingKey.Fab1DataBadge],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const secondaryIcon = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.Fab2DataIcon],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const secondaryLabel = useParseVariableText(
    settings?.[ComponentSettingKey.Fab2DataLabel],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const secondaryBadge = useParseVariableText(
    settings?.[ComponentSettingKey.Fab2DataBadge],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const primaryActive = settings?.[ComponentSettingKey.Fab1DataActive] as boolean
  const secondaryActive = settings?.[ComponentSettingKey.Fab2DataActive] as boolean

  // ========================= design =========================
  // Process general settings
  const hAlign = (settings?.[ComponentSettingKey.FabDesignHAlign] ?? 'end') as Alignment

  // Process primary fab design
  const primaryColorVariant = (settings?.[ComponentSettingKey.Fab1DesignColorVariant] as ColorVariant) ?? 'primary-40'
  const primaryShape = (settings?.[ComponentSettingKey.Fab1DesignShape] as FABCornerRadius) ?? 'rounded'
  const primaryShadow = settings?.[ComponentSettingKey.Fab1DesignShadow] as string
  const primarySize = settings?.[ComponentSettingKey.Fab1DesignSize] as string

  // Process secondary fab design
  const secondaryColorVariant =
    (settings?.[ComponentSettingKey.Fab2DesignColorVariant] as ColorVariant) ?? 'secondary-40'
  const secondaryShape = (settings?.[ComponentSettingKey.Fab2DesignShape] as FABCornerRadius) ?? 'rounded'
  const secondaryShadow = settings?.[ComponentSettingKey.Fab2DesignShadow] as string
  const secondarySize = settings?.[ComponentSettingKey.Fab1DesignSize] as string

  // Process typography
  const fabTypoSettings = settings[ComponentSettingKey.FabDesignTypography] as TypographyGroupValue
  const primaryLabelTypo = fabTypoSettings?.[ComponentSettingKey.Fab1DesignTypography]
  const secondaryLabelTypo = fabTypoSettings?.[ComponentSettingKey.Fab2DesignTypography]

  const hoverEffect = settings?.[ComponentSettingKey.FabDesignHoverEffect] as HoverEffectVariant

  // ========================= action =========================
  const primaryActions = settings?.[ComponentSettingKey.Fab1ActionClick] as IActionRepeaterValue[]
  const secondaryActions = settings?.[ComponentSettingKey.Fab2ActionClick] as IActionRepeaterValue[]

  const handlePrimaryClick = () => {
    try {
      if (component?._id) setAccordions('data', component._id, 'Primary')

      setActiveComponentAccordion('Primary')
      openComponentSettings(component)

      if (isDevelopmentMode) return
      if (!!primaryActions?.length) {
        simulateAction({
          actions: primaryActions,
          elRef: primaryElRef,
        })
      }
    } catch (error) {
      console.error('Error in handlePrimaryClick:', error)
    }
  }

  const handleSecondaryClick = () => {
    try {
      if (component?._id) setAccordions('data', component._id, 'Secondary')

      setActiveComponentAccordion('Secondary')
      openComponentSettings(component)

      if (isDevelopmentMode) return
      if (!!secondaryActions?.length) {
        simulateAction({
          actions: secondaryActions,
          elRef: secondaryElRef,
        })
      }
    } catch (error) {
      console.error('Error in handleSecondaryClick:', error)
    }
  }

  return children({
    isHidden,
    hAlign,
    primaryActive,
    secondaryActive,
    primaryFab: {
      icon: primaryIcon,
      text: primaryLabel,
      backgroundColor: primaryColorVariant,
      cornerRadius: primaryShape,
      elevation: {
        default: 'xl',
        lowered: 'lg',
        none: 'none',
      }[primaryShadow] as Elevation,
      size: primarySize,
      badgeText: primaryBadge,
      onClick: handlePrimaryClick,
      textTypography: primaryLabelTypo,
    },
    secondaryFab: {
      icon: secondaryIcon,
      text: secondaryLabel,
      backgroundColor: secondaryColorVariant,
      cornerRadius: secondaryShape,
      elevation: {
        default: 'xl',
        lowered: 'lg',
        none: 'none',
      }[secondaryShadow] as Elevation,
      size: secondarySize,
      badgeText: secondaryBadge,
      onClick: handleSecondaryClick,
      textTypography: secondaryLabelTypo,
      devMode: isDevelopmentMode,
    },
    hoverEffect,
    hoverIntensity: 'md',
  })
}
